import type React from "react";
import { type ReactElement, cloneElement, useMemo, useState } from "react";

import { Link } from "react-router-dom";

import type { UserMe } from "simplydo/interfaces/types/userTypes";
import { styled } from "simplydo/react/styled";
import { Button, Dropdown, Popup, type SemanticICONS } from "simplydo/react/ui";

import util from "utils/utils";

type RawAction<ItemType> = {
  checkPermission?: string;
  checkPermissionScope?: string;
  checkEnabledFeatures?: Parameters<typeof util.organisationFeaturesEnabled>[1];
  superadminsBypassChecks?: boolean;
  isVisible?: boolean;
  checkSuperAdminPermission?: string;
  appear?: "single" | "multi" | "both";
} & (
  | {
      type?: "Item";
      name: string;
      icon?: SemanticICONS;
      onClick?: (items: ItemType[]) => void;
      tooltip?: React.ReactNode;
      link?: (items: ItemType[]) => string;
      disabled?: boolean;
      actions?: (
        | RawAction<ItemType>
        | ((items: ItemType[]) => RawAction<ItemType>)
      )[];
    }
  | { type: "Divider" }
);

export type Action<ItemType> =
  | RawAction<ItemType>
  | ((items: ItemType[]) => RawAction<ItemType>);

export type ActionDropdownProps<ItemType> = {
  actions?:
    | Action<ItemType>[]
    | ((currentItems: ItemType[]) => Action<ItemType>[]);
  items: ItemType[];
  type: "single" | "multi";
  user: UserMe | null;
  setDropdownOpen?: (open: boolean) => void;
  trigger?: ReactElement<{
    onClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  }>;
  subMenu?: {
    name: string;
    icon?: SemanticICONS;
    onClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    open: boolean;
  };
};

const SubMenuItem = styled(Dropdown.Item)`
  &&& {
    position: relative;
    cursor: pointer;
    display: block;
    border: none;
    height: auto;
    text-align: left;
    border-top: none;
    line-height: 1em;
    color: rgba(0, 0, 0, 0.87);
    padding: 0.78571429rem 1.14285714rem !important;
    font-size: 1rem;
    text-transform: none;
    font-weight: 400;
    box-shadow: none;
    -webkit-touch-callout: none;
    &:hover {
      background: rgba(0, 0, 0, 0.05);
      color: rgba(0, 0, 0, 0.95);
    }
  }
`;

const StyledDropdownMenu = styled(Dropdown.Menu)`
  &&&& {
    &.is-submenu {
      right: calc(100% + 10px) !important;
      left: auto !important;
    }

    &:not(.is-submenu) {
      position: relative;
      top: 0;
      padding: 0;
      box-shadow: none;
      border: none;
    }

    .dropdown > .item > .flag,
    .dropdown > .item > .icon,
    .dropdown > .item > .image,
    .dropdown > .item > .label,
    .dropdown > .item > img {
      margin-left: 0;
      float: none;
      margin-right: 0.78571429rem;
    }
  }
`;

const ActionDropdown = <ItemType extends Record<string, unknown>>(
  props: ActionDropdownProps<ItemType>,
) => {
  const { user: rawUser, actions: propActions, setDropdownOpen } = props;
  const user = rawUser ?? undefined;

  // Maintain open state just for submenus
  const [subMenuOpen, setSubMenuOpen] = useState("");
  const [open, setOpen] = useState(false);

  const actions = useMemo(() => {
    if (typeof propActions === "function") {
      return propActions(props.items);
    }
    return propActions;
  }, [props.items, propActions]);

  const multiProps =
    !props.subMenu && props.type === "multi" && !props.trigger
      ? {
          icon: "caret down",
          trigger: (
            <Button
              icon="chevron down"
              compact
              size="small"
              style={{ whiteSpace: "nowrap" }}
              content={props.items.length}
            />
          ),
          style: {
            flexWrap: "nowrap",
            display: "flex",
            justifyContent: "flex-end",
            padding: 0,
          },
        }
      : {};

  if (props.items.length === 0) {
    return null;
  }

  if (!actions?.length) {
    return <></>;
  }

  const dropdownActions = actions ? (
    actions
      .map((rawAction) => {
        if (typeof rawAction === "function") {
          return rawAction(props.items);
        }
        return rawAction;
      })
      .filter((action) => {
        if (
          action.appear &&
          action.appear !== props.type &&
          action.appear !== "both"
        ) {
          return false;
        }
        if (
          action.superadminsBypassChecks &&
          util.hasPermission(user, "super.viewDashboard")
        ) {
          return true;
        }
        if (
          action.checkSuperAdminPermission &&
          util.hasPermission(user, action.checkSuperAdminPermission)
        ) {
          return true;
        }
        if (action.checkPermission) {
          if (!user) {
            return false;
          }
          if (
            !util.hasPermission(
              user,
              action.checkPermission,
              action.checkPermissionScope ??
                user.ownerOrganisation?._id ??
                user.organisation,
            )
          ) {
            return false;
          }
        }
        if (action.checkEnabledFeatures) {
          if (!user) {
            return false;
          }
          if (
            !util.organisationFeaturesEnabled(user, action.checkEnabledFeatures)
          ) {
            return false;
          }
        }
        return true;
      })
      .map((action, index) => {
        if (action.isVisible === false) {
          return null;
        }
        if (action.type === "Divider") {
          return <Dropdown.Divider key={`divider-${index}`} />;
        }
        const HoverWrapper: React.FC<{ children?: ReactElement }> = ({
          children,
        }) => (
          <Popup
            content={<div>{action.tooltip}</div>}
            trigger={children}
            position="top center"
            disabled={!action.tooltip}
          />
        );

        if (action.actions) {
          return (
            <HoverWrapper key={action.name}>
              <ActionDropdown
                key={action.name}
                subMenu={{
                  name: action.name,
                  icon: action.icon,
                  open: subMenuOpen === action.name,
                  onClick: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSubMenuOpen(
                      subMenuOpen === action.name ? "" : action.name,
                    );
                  },
                }}
                {...props}
                actions={action.actions}
              />
            </HoverWrapper>
          );
        }
        return (
          <HoverWrapper key={action.name}>
            <Dropdown.Item
              key={action.name}
              icon={action.icon}
              content={action.name}
              disabled={action.disabled}
              onClick={(e) => {
                if (action.onClick) {
                  e.preventDefault();
                  e.stopPropagation();
                  action.onClick(props.items);
                  setSubMenuOpen("");
                  setOpen(false);
                  setDropdownOpen?.(false);
                }
              }}
              {...(action.link
                ? { as: Link, to: action.link(props.items) }
                : {})}
            />
          </HoverWrapper>
        );
      })
  ) : (
    <Dropdown.Item>No actions configured</Dropdown.Item>
  );

  if (props.subMenu) {
    const subMenu = props.subMenu;
    return (
      <Dropdown
        icon={null}
        open={subMenu.open}
        onClose={() => {
          setSubMenuOpen("");
        }}
        trigger={
          <SubMenuItem
            icon={subMenu.icon ?? "caret left"}
            content={subMenu.name}
            onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) =>
              subMenu.onClick(e)
            } // set this submenu as active in parent
          />
        }
        pointing={"right"}
        direction="left"
        style={{ zIndex: 1000, width: "100%", padding: 0 }}
        disabled={props.items?.length === 0}
        {...multiProps}
      >
        {props.items?.length === 0 ? null : (
          <StyledDropdownMenu className="is-submenu">
            {dropdownActions}
          </StyledDropdownMenu>
        )}
      </Dropdown>
    );
  }

  return (
    <Popup
      on="click"
      basic
      position="bottom right"
      className="ui dropdown active"
      icon={null}
      open={open}
      onClose={() => {
        setSubMenuOpen("");
        setOpen(false);
        setDropdownOpen?.(false);
      }}
      onOpen={() => {
        setOpen(true);
        setDropdownOpen?.(true);
      }}
      trigger={
        props.trigger ? (
          cloneElement(props.trigger, {
            onClick: (e) => {
              e.preventDefault();
              e.stopPropagation();
              props.trigger?.props?.onClick?.(e);
            },
          })
        ) : (
          <Button
            compact
            style={{ margin: 0 }}
            icon="chevron down"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          />
        )
      }
      pointing={undefined}
      direction="left"
      style={{ zIndex: 1000, padding: 0 }}
      disabled={props.items?.length === 0}
      {...multiProps}
    >
      {props.items?.length === 0 ? null : (
        <StyledDropdownMenu className="visible">
          {dropdownActions}
        </StyledDropdownMenu>
      )}
    </Popup>
  );
};

export default ActionDropdown;
